import {useState} from 'react'
import {identityMissingLabel} from '@/utils/identityGate'
import CompleteProfileModal from '@/customComponents/uiComponents/global/CompleteProfileModal.jsx'

/**
 * "Your profile is incomplete — phone number is missing", with a way to fix it.
 *
 * ONE component for every place the gate can surface, so the passenger meets the same
 * sentence wherever they hit it: a refused booking, a refused confirm, a return booking,
 * and the profile page they get sent to. Three different wordings for one backend rule
 * would read like three different problems.
 *
 * `missing` comes from the server — `identity_missing` on the user, or `data.missing` on
 * the refusal — so the sentence names the actual field rather than guessing.
 *
 * "Complete your profile" opens CompleteProfileModal right here instead of navigating to the
 * profile page, so the passenger stays on the page they were on.
 *
 * The button is hidden (`showAction={false}`) on the profile page itself: EditProfile IS
 * the destination, and a button that navigates to the page you are already on reads as
 * broken. There the notice is the warning at the top and nothing more.
 */
export default function IdentityIncompleteNotice({missing, showAction = true, className = '', onCompleted}) {
    const [open, setOpen] = useState(false)

    return (
        <div
            role="alert"
            className={`rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 ${className}`}
        >
            <p className="font-semibold">Your profile is incomplete</p>
            <p className="mt-0.5">
                Your {identityMissingLabel(missing)} {Array.isArray(missing) && missing.length > 1 ? 'are' : 'is'} missing. We need {Array.isArray(missing) && missing.length > 1 ? 'them' : 'it'} before you can
                book — your driver uses it to reach you about the ride.
            </p>

            {showAction && (
                <button
                    type="button"
                    className="mt-2 font-semibold underline underline-offset-2"
                    onClick={() => setOpen(true)}
                >
                    Complete your profile
                </button>
            )}

            {/* The fix happens HERE, over the page that found the gap — no trip to /settings. */}
            <CompleteProfileModal
                missing={Array.isArray(missing) ? missing : []}
                isOpen={open}
                onClose={() => setOpen(false)}
                onCompleted={onCompleted}
            />
        </div>
    )
}
